import React, { useEffect } from 'react';
import { PortalCoreContext } from '../context/PortalCoreContext';
import { usePortalStore } from '../store/portal-store';
import { useUserIdentitySync } from '../hooks/useUserIdentitySync';
import type { ResolvedPortalConfig } from '../types/portal-config';

type Props = {
  config: ResolvedPortalConfig;
  children: React.ReactNode;
};

/**
 * Helper to access config from context inside hooks.
 */
const usePortalConfigInternal = () => {
  const ctx = React.useContext(PortalCoreContext);
  if (!ctx) {
    throw new Error('usePortalConfigInternal must be used inside PortalCoreProvider');
  }
  return ctx;
};

/**
 * Internal component that calls useUserIdentitySync and applies role-based locale filtering.
 */
const IdentitySyncBridge: React.FC = () => {
  useUserIdentitySync();
  const { userIdentity } = usePortalStore();
  const config = usePortalConfigInternal();
  const setAvailableLocales = usePortalStore((s) => s.setAvailableLocales);

  // Apply role-based locale filter when identity syncs or role changes.
  useEffect(() => {
    if (config.filterLocalesByRole) {
      const userRole = userIdentity?.role ?? null;
      const filtered = config.filterLocalesByRole(userRole);
      setAvailableLocales(filtered);
    } else {
      // If no filter defined, all locales are available.
      setAvailableLocales(config.locales);
    }
  }, [userIdentity, config, setAvailableLocales]);

  return null;
};

/**
 * Initializes portal-wide session state (locale, language, user identity) and makes
 * the resolved config available to all child hooks.
 *
 * Must wrap the router so locale-aware hooks have access to navigation context.
 *
 * @example
 * ```tsx
 * // App.tsx
 * import { PortalCoreProvider } from '@lucid/portal-core';
 * import { portalConfig } from './config/portal.config';
 *
 * <QueryClientProvider client={queryClient}>
 *   <AuthProvider {...auth0Config}>
 *     <PortalCoreProvider config={portalConfig}>
 *       <RouterProvider router={router} />
 *     </PortalCoreProvider>
 *   </AuthProvider>
 * </QueryClientProvider>
 * ```
 */
export const PortalCoreProvider: React.FC<Props> = ({ config, children }) => {
  const setLocale = usePortalStore((s) => s.setLocale);
  const setLanguage = usePortalStore((s) => s.setLanguage);

  // Seed defaults on first mount if store has no persisted values yet.
  // The store persists to '@lucid/portal-core' so this only runs on first-ever load.
  useEffect(() => {
    const current = usePortalStore.getState();
    if (current.locale === 'de-de' && config.defaultLocale !== 'de-de') {
      setLocale(config.defaultLocale);
    }
    if (current.language === null && config.defaultLanguage !== null) {
      setLanguage(config.defaultLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PortalCoreContext.Provider value={config}>
      <IdentitySyncBridge />
      {children}
    </PortalCoreContext.Provider>
  );
};
